import { Injectable } from 'framework/di';
import { debounce } from 'framework/functional';

import { DomHelper } from '../../helpers/dom.helper';
import { DTOHelper } from '../../helpers/dto.helper';
import { NavigationHelper } from '../../helpers/navigation.helper';
import { AppService } from '../app/app.service';
import { PlatformLoader } from '../platform/platform.loader';
import type {
  FormattedPriceResult,
  PriceDisplaySettings,
  PriceInfo,
  ProductDataFromDOM,
  ProductItemVariant,
} from './product.model';

export interface ProductUrlOptions {
  handle: string;
  variants?: ProductItemVariant[];
  variantId?: string | number;
  splitProduct?: boolean;
  addCollectionToUrl?: boolean;
}

@Injectable
export class ProductService {
  constructor(
    protected appService: AppService,
    protected domHelper: DomHelper,
    protected platformLoader: PlatformLoader,
    protected dtoHelper: DTOHelper,
    protected navigationHelper: NavigationHelper
  ) {}

  handleProductItemClick(
    target: HTMLElement,
    options: {
      addCollectionToProductUrl?: boolean;
    } = {}
  ): void {
    if (this.shouldIgnoreClick(target)) return;

    const productItem = target.closest('.boost-sd__product-item') as HTMLElement | null;
    if (!productItem) return;

    const productData = this.parseProductData(productItem);
    if (!productData?.handle) return;

    const productUrl = this.buildProductUrlWithVariant({
      handle: productData.handle,
      variants: productData.variants,
      variantId: productData.variantId,
      splitProduct: productData.splitProduct,
      addCollectionToUrl: options.addCollectionToProductUrl,
    });

    this.navigationHelper.setWindowLocation(productUrl);
  }

  protected shouldIgnoreClick(target: HTMLElement): boolean {
    if (target.closest('.boost-sd__product-banner-link')) {
      const bannerElement = target.closest('.boost-sd__product-banner-link') as HTMLAnchorElement;
      const url = bannerElement.getAttribute('href');
      const openNewTab = bannerElement.getAttribute('target') === '_blank';

      if (url) {
        if (openNewTab) {
          window.open(url, '_blank')?.focus();
        } else {
          this.navigationHelper.setWindowLocation(url);
        }
      }
      return true;
    }

    if (target.closest('.boost-sd__product-item--embedded-bundle')) return true;
    if (target.closest('.boost-sd__product-item--volume-bundle')) return true;

    const tagName = target.tagName?.toLowerCase();
    if (
      tagName === 'wishlist-button-collection' ||
      target.closest('wishlist-button-collection') ||
      target.dataset?.noRedirect === 'true' ||
      target.closest("[data-no-redirect='true']")
    ) {
      return true;
    }

    return false;
  }

  transformProductPrices(
    rootElement: Document | HTMLElement,
    blockType: 'filter' | 'recommendation' | 'search'
  ): void {
    if (blockType === 'filter') {
      const productList = rootElement.querySelector('.boost-sd__product-list');
      if ((productList as HTMLElement)?.dataset?.productPrice === 'true') return;
    }

    if (blockType === 'recommendation') {
      const recommendation = rootElement.querySelector('.boost-sd__recommendation');
      if ((recommendation as HTMLElement)?.dataset?.productPrice === 'true') return;
    }

    const productItems = rootElement.querySelectorAll(
      '.boost-sd__product-item, .boost-sd__product-item-list-view-layout'
    );

    const priceSettings = this.getPriceDisplaySettings();
    const translations = this.getProductTranslations();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(async (entry) => {
        if (!entry.isIntersecting) return;

        const productItem = entry.target as HTMLElement;
        const productPrice = productItem.querySelector('.boost-sd__product-price');
        if (!productPrice) return;

        const productData = this.parseProductData(productItem);
        if (!productData) return;

        const isSamePrice =
          `${productData.priceMin}-${productData.compareAtPriceMin}` ===
          `${productData.priceMax}-${productData.compareAtPriceMax}`;

        const hasMultiVariantPrice =
          /\{\{(?:\s*minPrice\s*|\bminPrice\b)\}}|{{\s*maxPrice\s*|\bmaxPrice\b}}/i.test(
            translations.amountTranslation || ''
          );

        const noPriceMax = isSamePrice || !hasMultiVariantPrice;

        const formattedPrices = this.formatPriceWithSettings(
          productData.priceMin || 0,
          productData.compareAtPriceMin || null,
          noPriceMax ? null : productData.priceMax || null,
          {
            ...priceSettings,
            productItemAmountTranslation: translations.amountTranslation,
            productItemSavingAmountTranslation: translations.savingAmountTranslation,
          }
        );

        const priceHTML = this.buildPriceHTML(formattedPrices, priceSettings);

        if (priceHTML && /\d/.test(priceHTML)) {
          productPrice.innerHTML = priceHTML;
        }

        observer.unobserve(productItem);
      });
    });

    productItems.forEach((item) => {
      observer.observe(item);
    });
  }

  buildProductUrlsWithLocale(rootElement: Document | HTMLElement): void {
    const productItems = rootElement.querySelectorAll(
      '.boost-sd__product-item, .boost-sd__product-item-list-view-layout'
    );

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const productItem = entry.target as HTMLElement;
        const productData = this.parseProductData(productItem);
        if (!productData?.handle) return;

        const productLinks =
          productItem.querySelectorAll<HTMLAnchorElement>('.boost-sd__product-link');
        const localePrefix = this.getLocalePrefix();

        productLinks.forEach((link) => {
          link.setAttribute('href', `${localePrefix}/products/${productData.handle}`);
        });

        observer.unobserve(productItem);
      });
    });

    productItems.forEach((item) => {
      observer.observe(item);
    });
  }

  setUniformProductImageHeight(
    rootElement: Document | HTMLElement,
    config: {
      mobileItemsPerRow: number;
      desktopItemsPerRow: number;
      mobileBreakPoint: number;
      tabletPortraitBreakPoint: number;
      tabletPortraitItemsPerRow: number;
    }
  ): void {
    const adjustProductImageHeights = () => {
      const productImageWrappers = rootElement.querySelectorAll(
        '.boost-sd__product-image-wrapper--auto'
      );

      if (productImageWrappers.length === 0) return;

      const productItems = Array.from(rootElement.querySelectorAll('.boost-sd__product-item'));

      let numberProductPerRow = config.mobileItemsPerRow;

      if (window.innerWidth > config.tabletPortraitBreakPoint) {
        numberProductPerRow = config.desktopItemsPerRow;
      } else if (window.innerWidth > config.mobileBreakPoint) {
        numberProductPerRow = config.tabletPortraitItemsPerRow;
      }

      const setAspectRatioForRow = (itemsInRow: Element[]) => {
        let maxRatio = 0;

        const updateDimensions = (img: HTMLImageElement) => {
          const { naturalHeight, naturalWidth } = img;
          const ratio = naturalHeight / naturalWidth;

          if (ratio > maxRatio) {
            maxRatio = ratio;
          }

          itemsInRow.forEach((item) => {
            const wrapper = item.querySelector<HTMLElement>(
              '.boost-sd__product-image-wrapper--auto'
            );
            if (wrapper) {
              wrapper.style.aspectRatio = `1/${maxRatio}`;
            }
          });
        };

        itemsInRow.forEach((item) => {
          const img = item.querySelector<HTMLImageElement>(
            '.boost-sd__product-image-wrapper--auto img'
          );
          if (img) {
            if (img.complete) {
              updateDimensions(img);
            } else {
              img.onload = () => updateDimensions(img);
            }
          }
        });
      };

      for (let i = 0; i < productItems.length; i += numberProductPerRow) {
        const itemsInRow = productItems.slice(i, i + numberProductPerRow);
        setAspectRatioForRow(itemsInRow);
      }
    };

    const debouncedAdjust = debounce(adjustProductImageHeights, 200);
    window.addEventListener('resize', debouncedAdjust);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', adjustProductImageHeights);
    } else {
      adjustProductImageHeights();
    }
  }

  styleBottomCTALayout8(rootElement: Document | HTMLElement): void {
    const productItems = rootElement.querySelectorAll('.boost-sd__product-item--sublayout-8-2');

    productItems.forEach((item) => {
      const ctaWrapper = item.querySelector<HTMLElement>('.boost-sd__cta-wrapper');

      item.addEventListener('mouseenter', () => {
        if (ctaWrapper) {
          const ctaHeight = ctaWrapper.offsetHeight;
          ctaWrapper.style.bottom = `-${ctaHeight - 15}px`;
        }
      });

      item.addEventListener('mouseleave', () => {
        if (ctaWrapper) {
          ctaWrapper.style.bottom = '';
        }
      });
    });
  }

  protected parseProductData(productItem: HTMLElement): ProductDataFromDOM | null {
    const dataAttr = productItem.getAttribute('data-product');
    if (!dataAttr) return null;

    return this.dtoHelper.safeParseJSON<ProductDataFromDOM>(dataAttr);
  }

  protected getPriceDisplaySettings(): PriceDisplaySettings {
    const themeSettings = this.appService.themeSettings;
    const priceConfig = themeSettings?.productItems?.productInfo?.elements?.price || {};
    const textAlign = themeSettings?.productItems?.productInfo?.textAlign;

    return {
      showCentAsSuperscript: priceConfig.showCentAsSuperscript,
      showCurrencyCodes: priceConfig.showCurrencyCodes,
      compareAtPricePosition: priceConfig.compareAtPricePosition,
      showSavingDisplay: priceConfig.showSavingDisplay,
      textAlign,
      removePriceDecimal: priceConfig.removePriceDecimal,
    };
  }

  protected getProductTranslations(): {
    amountTranslation?: string;
    savingAmountTranslation?: string;
  } {
    const translation = this.appService.translation?.productItem as
      | Record<string, string>
      | undefined;

    return {
      amountTranslation: translation?.amount,
      savingAmountTranslation: translation?.savingAmount,
    };
  }

  protected buildPriceHTML(
    formattedPrices: FormattedPriceResult,
    settings: PriceDisplaySettings
  ): string {
    const { priceValueWithFormat, compareAtPriceWithFormat, savingPriceWithFormat, isSale } =
      formattedPrices;
    const { compareAtPricePosition, textAlign } = settings;

    const saleClass = isSale ? ' boost-sd__product-price--sale' : '';
    const alignClass = textAlign ? ` boost-sd__product-price--${textAlign}` : '';

    let html = `<div class="boost-sd__product-price${saleClass}${alignClass}">`;

    if (compareAtPricePosition === 'before' && compareAtPriceWithFormat) {
      html += `<span class="boost-sd__product-price--compare"><s>${compareAtPriceWithFormat}</s></span>`;
    }

    html += `<span class="boost-sd__product-price--default">${priceValueWithFormat}</span>`;

    if (compareAtPricePosition !== 'before' && compareAtPriceWithFormat) {
      html += `<span class="boost-sd__product-price--compare"><s>${compareAtPriceWithFormat}</s></span>`;
    }

    if (savingPriceWithFormat) {
      html += `<span class="boost-sd__product-price--saving">${savingPriceWithFormat}</span>`;
    }

    html += '</div>';

    return html;
  }

  calculatePriceInfo(priceMin: number, compareAtPriceMin: number): PriceInfo {
    const price = Number(priceMin) || 0;
    const compareAt = Number(compareAtPriceMin) || 0;
    const isSale = compareAt > price;

    return {
      isSale,
      salePercent:
        isSale && compareAt ? `${Math.round(((compareAt - price) * 100) / compareAt)}%` : undefined,
      saleAmount: isSale ? compareAt - price : undefined,
    };
  }

  formatPrice(
    value: number | string | null | undefined,
    options: {
      showCurrencyCodes?: boolean;
      showCentAsSuperscript?: boolean;
      removeDecimalPoint?: boolean;
      removePriceDecimal?: boolean;
    } = {}
  ): string | null {
    if (value === null || value === undefined) return null;

    return this.appService.formatCurrency(Number(value), {
      showCurrencyCodes: options.showCurrencyCodes,
      showCentAsSuperscript: options.showCentAsSuperscript,
      removePriceDecimal: options.removePriceDecimal,
      removeDecimalPoint: options.removeDecimalPoint,
    });
  }

  formatPriceWithSettings(
    priceMin: number | string,
    compareAtPriceMin: number | string | null,
    priceMax: number | string | null,
    settings: PriceDisplaySettings & {
      productItemAmountTranslation?: string;
      productItemSavingAmountTranslation?: string;
    }
  ): FormattedPriceResult {
    const { showCentAsSuperscript, showCurrencyCodes, showSavingDisplay, removePriceDecimal } =
      settings;
    const priceInfo = this.calculatePriceInfo(Number(priceMin), Number(compareAtPriceMin) || 0);

    const formatOptions = {
      showCurrencyCodes,
      showCentAsSuperscript,
      removeDecimalPoint: showCentAsSuperscript,
      removePriceDecimal,
    };

    const minPriceFormatted = this.formatPrice(priceMin, formatOptions) || '';
    const maxPriceFormatted = priceMax ? this.formatPrice(priceMax, formatOptions) : null;

    const priceValueWithFormat =
      maxPriceFormatted && settings.productItemAmountTranslation
        ? this.translateWithComponent(settings.productItemAmountTranslation, {
            minPrice: minPriceFormatted,
            maxPrice: maxPriceFormatted,
          })
        : minPriceFormatted;

    const compareAtPriceWithFormat =
      !compareAtPriceMin || !priceInfo.isSale
        ? null
        : this.formatPrice(compareAtPriceMin, formatOptions);

    const savingPriceWithFormat =
      !showSavingDisplay || !priceInfo.saleAmount || !settings.productItemSavingAmountTranslation
        ? null
        : this.translateWithComponent(settings.productItemSavingAmountTranslation, {
            salePercent: priceInfo.salePercent || '',
            saleAmount: this.formatPrice(priceInfo.saleAmount, { showCurrencyCodes }) || '',
          });

    return {
      priceValueWithFormat,
      compareAtPriceWithFormat,
      savingPriceWithFormat,
      isSale: priceInfo.isSale,
    };
  }

  protected translateWithComponent(template: string, variables: Record<string, string>): string {
    let result = template;
    for (const [key, value] of Object.entries(variables)) {
      result = result.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), value);
    }
    return result;
  }

  buildProductUrl(options: ProductUrlOptions): string {
    const { handle, addCollectionToUrl = false } = options;

    if (!handle) return '/';

    const localePrefix = this.getLocalePrefix();
    const pathname = window.location.pathname;

    if (!addCollectionToUrl) {
      return `${localePrefix}/products/${handle}`;
    }

    const collectionHandle = this.extractCollectionHandle(pathname);
    if (collectionHandle) {
      return `${localePrefix}/collections/${collectionHandle}/products/${handle}`;
    }

    return `${localePrefix}/products/${handle}`;
  }

  buildProductUrlWithVariant(options: ProductUrlOptions): string {
    const baseUrl = this.buildProductUrl(options);
    const { variantId, splitProduct, variants } = options;

    const shouldAddVariant = variantId || (splitProduct && variants);
    if (!shouldAddVariant) return baseUrl;

    return `${baseUrl}?variant=${variantId}`;
  }

  getLocalePrefix(): string {
    const routes = this.platformLoader.platform.router.routes;
    return (routes?.root || '/').replace(/\/$/, '');
  }

  protected extractCollectionHandle(pathname: string): string | null {
    const elements = pathname.split('/');
    const router = this.platformLoader.platform.router;
    const page = router.page;

    if (pathname === '/' || page === 'search') {
      return 'all';
    }

    if (page === 'vendor' || page === 'types' || page === 'page') {
      return 'all';
    }

    if (page === 'tag') {
      const collectionIndex = elements.indexOf('collections') + 1;
      if (elements.length >= 4 && elements[collectionIndex]) {
        return elements[collectionIndex];
      }
      return null;
    }

    if (this.isGoogleCacheUrl()) {
      return this.extractFromGoogleCache();
    }

    if (elements.includes('collections')) {
      const collectionIndex = elements.indexOf('collections') + 1;
      return elements[collectionIndex] || 'all';
    }

    return null;
  }

  protected isGoogleCacheUrl(): boolean {
    const params = window.location.search.substring(1);
    return params.indexOf('cache:') > -1;
  }

  protected extractFromGoogleCache(): string {
    const params = window.location.search.substring(1);
    let collectionHandle = 'all';

    const temp = params.split('&')[0].split('?')[0].split('collections/');
    if (temp.length > 1) {
      if (temp[1].indexOf('/') > -1) {
        collectionHandle = temp[1].split('/')[0];
      } else {
        collectionHandle = temp[1];
      }
    }

    return collectionHandle.replace(/[`~!@#$%^&*()_|+=?;:'",.<>{}[\]\\/]/g, '') || 'all';
  }
}
